import {
    ChangeEvent,
    FC,
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import debounce from 'lodash/debounce';
import { Loader2, Search, X } from 'lucide-react';

import { cn } from '../../utils';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface Props {
    readonly query: string;
    readonly loading: boolean;
    readonly onSearch: (query: string) => void;
    readonly placeholder: string;
    readonly inputTestId: string;
    readonly submitLabel?: string;
    readonly className?: string;
    readonly inputClassName?: string;
}

export const DebouncedSearchInput: FC<Props> = ({
    query,
    loading,
    onSearch,
    placeholder,
    inputTestId,
    submitLabel,
    className,
    inputClassName,
}) => {
    const [value, setValue] = useState(query);
    const onSearchRef = useRef(onSearch);

    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    useEffect(() => {
        setValue(query);
    }, [query]);

    const debouncedSearch = useMemo(
        () =>
            debounce((nextQuery: string) => {
                onSearchRef.current(nextQuery);
            }, 350),
        [],
    );

    useEffect(
        () => () => {
            debouncedSearch.cancel();
        },
        [debouncedSearch],
    );

    const runSearch = (nextQuery: string) => {
        const normalizedQuery = nextQuery.trim();

        debouncedSearch.cancel();
        if (normalizedQuery !== query.trim()) {
            onSearch(normalizedQuery);
        }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        const normalizedQuery = nextValue.trim();

        setValue(nextValue);
        if (normalizedQuery === query.trim()) {
            debouncedSearch.cancel();
            return;
        }

        debouncedSearch(normalizedQuery);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        runSearch(value);
    };

    const clearSearch = () => {
        setValue('');
        runSearch('');
    };

    return (
        <form
            className={cn('flex items-center gap-2', className)}
            onSubmit={handleSubmit}
            role="search"
        >
            <div className="relative min-w-0 flex-1">
                {loading ? (
                    <Loader2 className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                ) : (
                    <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                )}
                <Input
                    type="search"
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    className={cn('pr-10 pl-9', inputClassName)}
                    data-testid={inputTestId}
                />
                {value && (
                    <button
                        type="button"
                        onClick={clearSearch}
                        aria-label="Очистити пошук"
                        className="absolute top-1/2 right-2.5 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>
            {submitLabel && (
                <Button
                    type="submit"
                    className="h-11 rounded-xl px-5"
                    disabled={loading || value.trim() === query.trim()}
                >
                    {submitLabel}
                </Button>
            )}
        </form>
    );
};
